import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  RadioGroup,
  OutstandingLoansSection,
  EmployeeSearchInput,
  LeaveRecordsDisplay,
} from "./shared/components";
import {
  getEmployeeDetails,
  submitResignation,
  calculateNoticeEndDate,
  calculateShortfallDays,
  getResignationPrefill,
  uploadFileToS3,
  uploadResignationDoc,
  getDocumentUrl,
  formatDate,
  EmployeeDetails,
  ResignationRequest,
  ResignationPrefill,
  OutstandingLoan,
  BondType,
  DECLARED_LOAN_TYPES,
  LoanSuretyItem,
  EmployeeSearchResult,
} from "./services/eSeparation.service";

interface UploadedDoc {
  s3_key: string;
  file_name: string;
  url?: string;
}

const STEPS = [
  { label: "Employee", icon: "ti-user" },
  { label: "Hierarchy", icon: "ti-hierarchy" },
  { label: "Notice Period", icon: "ti-clock" },
  { label: "Early Relieving", icon: "ti-calendar-event" },
  { label: "Bond & Contact", icon: "ti-shield" },
  { label: "Reason & Docs", icon: "ti-file-text" },
  { label: "Declarations", icon: "ti-checklist" },
];

const SEPARATION_TYPE_OPTIONS: Array<{ key: string; label: string; icon: string; desc: string }> = [
  {
    key: "RESIGNATION",
    label: "Intimation / Resignation",
    icon: "ti-file-text",
    desc: "Standard resignation from regular service",
  },
  // Deputation / Retirement (superannuation) / Transfer-Out follow the No-Dues
  // workflow only — they are initiated from "Initiate No-Dues Separation", never
  // through this resignation (approval-flow) form.
];

const formatAddressDetail = (addr: any): string => {
  if (!addr) return "";
  if (typeof addr === "string") return addr.trim();
  if (typeof addr !== "object") return "";

  if (addr.formatted && typeof addr.formatted === "string" && addr.formatted.trim()) {
    return addr.formatted.trim();
  }

  const parts = [
    addr.care_of ? `C/o ${addr.care_of}` : null,
    addr.house_number || addr.house_no || addr.houseNo,
    addr.street || addr.street_1 || addr.address_line_1 || addr.addressLine1 || addr.address1 || addr.address_line1,
    addr.address_line_2 || addr.addressLine2 || addr.address2 || addr.address_line2,
    addr.city,
    addr.district,
    addr.state || addr.region,
    addr.postal_code || addr.pincode || addr.pin_code || addr.postalCode,
    addr.country
  ].filter(Boolean).map((p: any) => String(p).trim()).filter(Boolean);

  return parts.join(", ");
};

const IntimationResignation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = all_routes;

  const presetSeparationType = (location.state as any)?.separation_type as string | undefined;

  const [emp, setEmp] = useState<EmployeeDetails | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [separationType, setSeparationType] = useState(presetSeparationType || "RESIGNATION");

  const [bondTypes, setBondTypes] = useState<BondType[]>([]);
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [tempAddress, setTempAddress] = useState("");

  const [earlyRelieving, setEarlyRelieving] = useState("no");
  const [desiredDate, setDesiredDate] = useState("");

  const [reason, setReason] = useState("");
  const [noc, setNoc] = useState("no");
  const [nocPost, setNocPost] = useState("");
  const [nocAdvtNo, setNocAdvtNo] = useState("");
  const [nocDate, setNocDate] = useState("");
  // Any number of educational qualifications, each with its own supporting document.
  type EduRow = { key: number; name: string; doc: UploadedDoc | null; uploading: boolean };
  const eduKey = useRef(1);
  const [eduRows, setEduRows] = useState<EduRow[]>([{ key: 0, name: "", doc: null, uploading: false }]);
  const patchEdu = (key: number, patch: Partial<EduRow>) =>
    setEduRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addEdu = () =>
    setEduRows((rows) => [...rows, { key: eduKey.current++, name: "", doc: null, uploading: false }]);
  const removeEdu = (key: number) =>
    setEduRows((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows));
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDoc[]>([]);
  const [uploading, setUploading] = useState(false);

  const [outstandingLoans, setOutstandingLoans] = useState<OutstandingLoan[]>([]);
  // "Indicate Loan or Advances" (field 25) — the 7 fixed categories, amount
  // typed in by the employee. Keyed by DECLARED_LOAN_TYPES code.
  const [declaredLoans, setDeclaredLoans] = useState<Record<string, string>>({});
  const [otherLoanDescription, setOtherLoanDescription] = useState("");
  // Employee's corrected amount for a specific auto-fetched SAP loan, keyed
  // by that loan's reference_number — recorded separately, never written
  // back to sap_loan_requests.
  const [loanOverrides, setLoanOverrides] = useState<Record<string, string>>({});
  // "Your Sureties for Loans, if any" (field 26) — seeded from the real
  // sap_loan_sureties data via prefill.loan_sureties, then freely editable
  // (including the employee's own emp ID correction) and extensible.
  const [suretyRows, setSuretyRows] = useState<
    (LoanSuretyItem & { surety_emp_name?: string })[]
  >([]);
  const [declared, setDeclared] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [prefill, setPrefill] = useState<ResignationPrefill | null>(null);
  const [prefillLoading, setPrefillLoading] = useState(false);

  const [itemsInCustody, setItemsInCustody] = useState("no");
  const [itemsInCustodyText, setItemsInCustodyText] = useState("");
  const [tempChargeOfficer, setTempChargeOfficer] = useState("");
  const [foreignTrainingBond, setForeignTrainingBond] = useState("no");
  const [foreignTrainingPeriod, setForeignTrainingPeriod] = useState("");
  const [foreignTrainingAmount, setForeignTrainingAmount] = useState("");
  const [foreignTrainingDetails, setForeignTrainingDetails] = useState("");
  const [eolAvailed, setEolAvailed] = useState("no");
  const [eolPeriods, setEolPeriods] = useState([{ start: "", end: "" }]);
  const [withdrawLastYr, setWithdrawLastYr] = useState("no");
  const [withdrawLastYrDetails, setWithdrawLastYrDetails] = useState("");
  const [deputationAvailed, setDeputationAvailed] = useState("no");
  const [deputationRemittanceReceived, setDeputationRemittanceReceived] = useState("no");
  const [deputationHistory, setDeputationHistory] = useState<
    { organization_name: string; duration: string }[]
  >([{ organization_name: "", duration: "" }]);
  const [pendingCasesDetails, setPendingCasesDetails] = useState("");
  const [gratuityOption, setGratuityOption] = useState<"release" | "transfer">("release");
  const [bondSelections, setBondSelections] = useState<Record<number, { active: boolean, values: Record<string, any> }>>({});

  const [trainingProvided, setTrainingProvided] = useState("no");
  const [trainingDetails, setTrainingDetails] = useState("");
  const [otherBond, setOtherBond] = useState("no");
  const [otherBondDetails, setOtherBondDetails] = useState("");


  const today = new Date();
  const intimationDate = today.toISOString().split("T")[0];
  // Non-editable — always intimation date + 89 days.
  const [noticeEndIso] = useState(
    () => calculateNoticeEndDate(new Date()).toISOString().split("T")[0]
  );

  useEffect(() => {
    getEmployeeDetails().then((res) => {
      if (res.data) {
        setEmp(res.data);
        setMobile(res.data.mobile || "");
        setEmail(res.data.email || "");
        if (res.data.home_address) {
          setHomeAddress(res.data.home_address);
        }
      }
    });
    setPrefillLoading(true);
    getResignationPrefill()
      .then((res) => {
        if (res.data) {
          setPrefill(res.data);
          setBondTypes(res.data.bond_types || []);
          setOutstandingLoans(res.data.outstanding_loans || []);
          // Flatten each SAP loan-surety entry's firstSurety/secondSurety into
          // its own editable row, tagged with that loan's own type/text.
          const seededSureties: (LoanSuretyItem & { surety_emp_name?: string })[] = [];
          (res.data.loan_sureties || []).forEach((s) => {
            if (s.firstSurety?.empId) {
              seededSureties.push({
                loan_type: s.loanText || s.loanType,
                surety_emp_id: s.firstSurety.empId,
                surety_emp_name: s.firstSurety.name,
              });
            }
            if (s.secondSurety?.empId) {
              seededSureties.push({
                loan_type: s.loanText || s.loanType,
                surety_emp_id: s.secondSurety.empId,
                surety_emp_name: s.secondSurety.name,
              });
            }
          });
          setSuretyRows(seededSureties);
          const tempAddr = formatAddressDetail(res.data.temporary_residence_address);
          if (tempAddr) {
            setTempAddress(tempAddr);
          }
          const homeTownAddr = formatAddressDetail(res.data.home_town_address);
          if (homeTownAddr) {
            setHomeAddress(homeTownAddr);
          }
        }
      })
      .finally(() => setPrefillLoading(false));
  }, []);



  const shortfallDays = desiredDate
    ? calculateShortfallDays(noticeEndIso, desiredDate)
    : 0;

  // Section 03's displayed Notice End Date tracks the Desired Relieving Date
  // in real time once Early Relieving is requested — the underlying fixed
  // 89-day notice_end_date (noticeEndIso) is still what the shortfall above,
  // the date picker's max, and the submitted payload are based on.
  const displayedNoticeEndDate =
    earlyRelieving === "yes" && desiredDate ? desiredDate : noticeEndIso;

  // (Basic Pay + DA) / 30 × Shortfall Days. DA has no synced source yet, so
  // it's treated as 0 — mirrors EsepRequestService.computeShortNoticeAmount.
  const shortNoticeAmountPreview =
    shortfallDays > 0 ? ((prefill?.basic_pay || 0) / 30) * shortfallDays : 0;

  const addDeputationRow = () =>
    setDeputationHistory((rows) => [...rows, { organization_name: "", duration: "" }]);
  const removeDeputationRow = (index: number) =>
    setDeputationHistory((rows) => rows.filter((_, i) => i !== index));
  const updateDeputationRow = (
    index: number,
    field: "organization_name" | "duration",
    value: string
  ) =>
    setDeputationHistory((rows) =>
      rows.map((r, i) => (i === index ? { ...r, [field]: value } : r))
    );

  // "Child Care Leave (CCL) taken in last 2 years?" / "Leave Without Pay
  // (LWP) taken in last 2 years?" — always auto-fetched from SAP, never
  // employee-typed. CCL is shown only to female employees (gender "2" per
  // the SAP code convention already used elsewhere in the app).
  const isFemaleEmployee = emp?.gender === "2";
  const cclRecords = prefill?.ccl_records || [];
  const lwpRecords = prefill?.lwp_records || [];

  const buildPayload = (): ResignationRequest & Record<string, any> => {
    const payload: ResignationRequest & Record<string, any> = {
      emp_no: emp?.emp_no || "",
      separation_type: separationType,
      intimation_date: intimationDate,
      notice_end_date: noticeEndIso,
      bond_selections: Object.entries(bondSelections)
        .filter(([_, sel]) => sel.active)
        .map(([id, sel]) => ({
          bond_type_id: Number(id),
          values: sel.values
        })),
      is_bond: Object.values(bondSelections).some(s => s.active),
      bond_type_id: undefined,
      training_bond_amount: undefined,
      mobile,
      email,
      home_address: homeAddress || undefined,
      temp_address: tempAddress || undefined,
      early_relieving: earlyRelieving === "yes",
      reason,
      noc_available: noc === "yes",
      noc_post: noc === "yes" ? nocPost || undefined : undefined,
      noc_advt_no: noc === "yes" ? nocAdvtNo || undefined : undefined,
      noc_date: noc === "yes" ? nocDate || undefined : undefined,
      education_qual:
        eduRows
          .map((r) => r.name.trim())
          .filter(Boolean)
          .join(", ") || undefined,
      // "Indicate Loan or Advances" (field 25) — non-blank fixed-category
      // amounts, plus any corrected amounts for auto-fetched SAP loans.
      loans: [
        ...Object.entries(declaredLoans)
          .filter(([, amt]) => amt && Number(amt) > 0)
          .map(([code, amt]) => ({
            loan_type: code,
            amount: Number(amt),
            ...(code === "OTHERS" && otherLoanDescription
              ? { description: otherLoanDescription }
              : {}),
          })),
        ...Object.entries(loanOverrides)
          .filter(([, amt]) => amt !== "" && amt !== undefined)
          .map(([referenceNumber, amt]) => ({
            loan_type: "SAP_OVERRIDE",
            amount: Number(amt) || 0,
            reference_number: referenceNumber,
          })),
      ],
      // "Your Sureties for Loans, if any" (field 26) — recorded locally,
      // never written back to sap_loan_sureties.
      loan_sureties: suretyRows
        .filter((r) => r.surety_emp_id?.trim())
        .map((r) => ({ loan_type: r.loan_type, surety_emp_id: r.surety_emp_id.trim() })),
      items_in_custody: itemsInCustody === "yes",
      items_in_custody_text: itemsInCustodyText || undefined,
      temp_charge_officer: tempChargeOfficer || undefined,
      foreign_training_bond: foreignTrainingBond === "yes",
      foreign_training_period:
        foreignTrainingBond === "yes" ? foreignTrainingPeriod || undefined : undefined,
      foreign_training_bond_amount:
        foreignTrainingBond === "yes" ? Number(foreignTrainingAmount) || 0 : undefined,
      foreign_training_details:
        foreignTrainingBond === "yes" ? foreignTrainingDetails || undefined : undefined,
      // Auto-fetched from SAP (sap_lwp_records) — not employee-typed.
      ccl_availed: isFemaleEmployee && cclRecords.length > 0,
      ccl_period:
        isFemaleEmployee && cclRecords.length > 0
          ? cclRecords.map((r) => `${formatDate(r.start_date)} to ${formatDate(r.end_date)}`).join(", ")
          : undefined,
      lwp_availed: lwpRecords.length > 0,
      lwp_period:
        lwpRecords.length > 0
          ? lwpRecords.map((r) => `${formatDate(r.start_date)} to ${formatDate(r.end_date)}`).join(", ")
          : undefined,
      eol_availed: eolAvailed === "yes",
      eol_details: eolAvailed === "yes" ? eolPeriods.filter(p => p.start && p.end).map(p => `${formatDate(p.start)} to ${formatDate(p.end)}`).join(", ") : undefined,
      withdraw_last_yr: withdrawLastYr === "yes",
      withdraw_last_yr_details:
        withdrawLastYr === "yes" ? withdrawLastYrDetails || undefined : undefined,
      deputation_availed: deputationAvailed === "yes",
      deputation_remittance_received:
        deputationAvailed === "yes" ? deputationRemittanceReceived === "yes" : undefined,
      deputation_history:
        deputationAvailed === "yes"
          ? deputationHistory.filter((d) => d.organization_name.trim())
          : undefined,
      pending_cases_details: pendingCasesDetails || undefined,

      gratuity_transfer: gratuityOption === "transfer",
      // The non-compete undertaking (point III) is now folded into the single
      // unified declaration checkbox below, not a separate acknowledgment.
      non_compete_declaration: declared,
      training_provided: trainingProvided === "yes",
      training_details: trainingProvided === "yes" ? trainingDetails || undefined : undefined,
      other_bond_submitted: otherBond === "yes",
      other_bond_details: otherBond === "yes" ? otherBondDetails || undefined : undefined,
    };
    if (earlyRelieving === "yes") {
      payload.desired_relieve_date = desiredDate;
      payload.shortfall_days = shortfallDays;
    }
    return payload;
  };

  const goToStep = (i: number) => {
    setActiveStep(i);
    const el = document.getElementById(`esep-step-${i}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
  const ALLOWED_EXT = ["jpg", "jpeg", "png", "pdf"];

  const isAllowedFile = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    return ALLOWED_TYPES.includes(file.type) || ALLOWED_EXT.includes(ext);
  };

  const handleUpload = async (fl: FileList | null) => {
    if (!fl || fl.length === 0) return;
    setUploading(true);
    const toastId = toast.info("Uploading document...", { autoClose: false });
    try {
      const uploaded: UploadedDoc[] = [];
      for (const file of Array.from(fl)) {
        if (!isAllowedFile(file)) {
          toast.warning(
            `${file.name} is not allowed. Only JPG, PNG and PDF files are supported.`
          );
          continue;
        }
        if (file.size / (1024 * 1024) > 5) {
          toast.warning(`${file.name} is larger than 5 MB and was skipped.`);
          continue;
        }
        const s3 = await uploadFileToS3(file);
        if (!s3) throw new Error(`Failed to upload ${file.name}`);
        uploaded.push({ s3_key: s3.key, file_name: file.name, url: s3.url });
      }
      if (uploaded.length === 0) {
        toast.update(toastId, {
          render: "No valid file uploaded. Only JPG, PNG and PDF up to 5 MB are allowed.",
          type: "warning",
          autoClose: 4000,
        });
        return;
      }
      setUploadedDocs((prev) => [...prev, ...uploaded]);
      toast.update(toastId, {
        render: "Document uploaded successfully.",
        type: "success",
        autoClose: 3000,
      });
    } catch (e: any) {
      toast.update(toastId, {
        render: e?.message || "Failed to upload document.",
        type: "error",
        autoClose: 4000,
      });
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = (index: number) =>
    setUploadedDocs((prev) => prev.filter((_, i) => i !== index));

  const viewDoc = async (doc: UploadedDoc) => {
    let url = doc.url || "";
    if (!url) url = (await getDocumentUrl(doc.s3_key)) || "";
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Could not open this document. Please try again.");
    }
  };

  const handleEduQualUpload = async (key: number, fl: FileList | null) => {
    const file = fl?.[0];
    if (!file) return;
    if (!isAllowedFile(file)) {
      return toast.warning("Only JPG, PNG and PDF files are supported.");
    }
    if (file.size / (1024 * 1024) > 5) {
      return toast.warning("File is larger than 5 MB.");
    }
    patchEdu(key, { uploading: true });
    try {
      const s3 = await uploadFileToS3(file);
      if (!s3) throw new Error("Failed to upload education qualification doc");
      patchEdu(key, { doc: { s3_key: s3.key, file_name: file.name, url: s3.url } });
      toast.success("Education Qualification document uploaded successfully.");
    } catch (e: any) {
      toast.error(e?.message || "Failed to upload document.");
    } finally {
      patchEdu(key, { uploading: false });
    }
  };

  const extractRequestId = (res: any): string => {
    const d = res?.data ?? res;
    return (
      d?.request_id ||
      d?.data?.request_id ||
      d?.Response?.data?.request_id ||
      d?.Response?.request_id ||
      ""
    );
  };

  const handleSubmit = async () => {
    if (!reason.trim()) return toast.warning("Please provide a reason for resignation.");
    if (mobile.length < 10) return toast.warning("Please provide a valid 10-digit mobile number.");
    if (!email.includes("@")) return toast.warning("Please provide a valid email address.");
    if (noc === "yes" && uploadedDocs.length === 0)
      return toast.warning(
        "Please upload the NOC / supporting documents before submitting."
      );
    // Dynamic bond validation
    for (const bt of bondTypes) {
      const sel = bondSelections[bt.id];
      if (sel?.active) {
        if (!sel.values["training_cost"]) {
          return toast.warning(`Please provide Training Cost for ${bt.name}`);
        }
        for (const f of bt.fields || []) {
          if (f.is_required && !sel.values[f.field_key]) {
            return toast.warning(`Please provide ${f.field_label} for ${bt.name}`);
          }
        }
      }
    }
    if (foreignTrainingBond === "yes" && !foreignTrainingPeriod.trim())
      return toast.warning("Please provide the period of bond executed.");
    if (foreignTrainingBond === "yes" && !foreignTrainingAmount.trim())
      return toast.warning("Please provide the amount of bond executed.");
    if (foreignTrainingBond === "yes" && !foreignTrainingDetails.trim())
      return toast.warning("Please provide the details of training.");
    if (eolAvailed === "yes" && !eolPeriods.some(p => p.start && p.end))
      return toast.warning("Please provide the EOL duration and exact dates.");
    if (withdrawLastYr === "yes" && !withdrawLastYrDetails.trim())
      return toast.warning(
        "Please provide details of the withdrawn resignation."
      );
    if (
      deputationAvailed === "yes" &&
      !deputationHistory.some((d) => d.organization_name.trim())
    )
      return toast.warning(
        "Please provide at least one deputation organization and duration."
      );
    if (!declared) return toast.warning("Please accept the declaration before submitting.");
    setSubmitting(true);
    const res = await submitResignation(buildPayload());
    if (res.success) {
      const newRequestId = extractRequestId(res);
      const eduDocs = eduRows.map((r) => r.doc).filter((d): d is UploadedDoc => !!d);
      if (newRequestId && (uploadedDocs.length > 0 || eduDocs.length > 0)) {
        await Promise.all(
          [...uploadedDocs, ...eduDocs].map((d) =>
            uploadResignationDoc({
              request_id: newRequestId,
              doc_type: "QUALIFICATION",
              s3_key: d.s3_key,
              file_name: d.file_name,
            }).catch(() => null)
          )
        );
      }

      setSubmitting(false);
      toast.success(
        "Your resignation intimation has been submitted successfully. You will receive an acknowledgment shortly."
      );
      navigate(routes.Esepration.overview);
    } else {
      setSubmitting(false);
      toast.error(res.message || "Failed to submit. Please try again.");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content pb-5">
        {/* PAGE HEADER */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto">
            <h3 className="page-title fw-bold text-dark mb-1">
              Application for Intimation / Resignation
            </h3>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.Esepration.hub} className="text-decoration-none text-muted">
                    E-Separation
                  </Link>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  Intimation &amp; Resignation Form
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2 px-3"
              onClick={() => navigate(-1)}
            >
              <i className="ti ti-arrow-left"></i> Back
            </button>
          </div>
        </div>

        {/* STEPPER INDICATOR (MATCHING 7 SECTIONS) */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-3 px-md-4">
            <div className="d-flex align-items-center justify-content-between flex-nowrap overflow-x-auto gap-1">
              {STEPS.map((s, i) => {
                const done = i < activeStep;
                const active = i === activeStep;
                return (
                  <React.Fragment key={s.label}>
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none d-flex align-items-center gap-2 p-0 text-start flex-shrink-0"
                      onClick={() => goToStep(i)}
                      style={{ cursor: "pointer" }}
                    >
                      <span
                        className="d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 14,
                          fontSize: 12,
                          background: done ? "#E1F5EE" : active ? "#0d6efd" : "#f1f3f5",
                          border: done ? "2px solid #049123" : active ? "2px solid #0d6efd" : "1px solid #ced4da",
                          color: done ? "#049123" : active ? "#fff" : "#495057",
                        }}
                      >
                        {done ? <i className="ti ti-check fs-7 fw-bold" /> : i + 1}
                      </span>
                      <span
                        style={{
                          fontSize: "12.5px",
                          fontWeight: active ? 700 : done ? 600 : 500,
                          color: active ? "#0d6efd" : done ? "#212529" : "#495057",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.label}
                      </span>
                    </button>
                    {i < STEPS.length - 1 && (
                      <span
                        className="flex-grow-1 mx-2 d-none d-md-block"
                        style={{
                          height: 2,
                          minWidth: 16,
                          background: done ? "#049123" : "#dee2e6",
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* SECTION 01: SUBJECT EMPLOYEE DETAILS (STEP 1) */}
        <div id="esep-step-0">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  01
                </span>
                Subject Employee Details
              </h5>
            </div>
            <div className="card-body p-4">
              {/* Type of Separation Sub-block */}
              <div className="mb-4 pb-3 border-bottom">
                <label className="form-label fw-semibold text-secondary small mb-2">
                  What are you applying for? <span className="text-danger">*</span>
                </label>
                <div className="row g-3">
                  {SEPARATION_TYPE_OPTIONS.map((opt) => {
                    const selected = separationType === opt.key;
                    return (
                      <div className="col-md-4" key={opt.key}>
                        <button
                          type="button"
                          className="btn w-100 text-start p-3 h-100 position-relative"
                          style={{
                            borderRadius: 10,
                            border: `2px solid ${selected ? "#0d6efd" : "#e9ecef"}`,
                            background: selected ? "#f0f7ff" : "#fff",
                            transition: "all 0.2s ease-in-out",
                          }}
                          onClick={() => setSeparationType(opt.key)}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span
                              className="d-inline-flex align-items-center justify-content-center rounded-3 p-2"
                              style={{
                                background: selected ? "#0d6efd" : "#f8f9fa",
                                color: selected ? "#fff" : "#0d6efd",
                              }}
                            >
                              <i className={`ti ${opt.icon} fs-5`} />
                            </span>
                            {selected && (
                              <span className="badge bg-primary rounded-circle p-1">
                                <i className="ti ti-check" style={{ fontSize: 12 }} />
                              </span>
                            )}
                          </div>
                          <div className={`fw-bold mb-1 ${selected ? "text-primary" : "text-dark"}`}>
                            {opt.label}
                          </div>
                          <div className="text-muted small" style={{ fontSize: 12 }}>
                            {opt.desc}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Master Data Grid */}
              <div
                className="alert alert-info border-0 d-flex align-items-center gap-2 mb-4 py-2 px-3 rounded-3"
                style={{ background: "#e8f4fd", color: "#0c5460" }}
              >
                <i className="ti ti-info-circle fs-5 flex-shrink-0" />
                <span className="small">
                  These details are auto-filled from SAP HR Master Data and cannot be edited.
                </span>
              </div>

              <div className="row g-3">
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Employee Name
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.emp_name || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Employee No.
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.emp_no || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Designation
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.designation || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Regular / Contract
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.emp_type || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.posting || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Place of Posting
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.place_of_posting || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Wing
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.wing || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Pay Scale
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.pay_scale || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Date of Joining
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.doj ? formatDate(emp.doj) : "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Customer Specific Status
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={prefill?.emp_type || emp?.emp_type || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Basic Pay
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={emp?.basic_pay_daily_rate || "—"}
                    readOnly
                  />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Home Address
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={
                      homeAddress ||
                      formatAddressDetail(prefill?.home_town_address) ||
                      emp?.home_address ||
                      "—"
                    }
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 02: APPROVAL HIERARCHY (STEP 2) */}
        <div id="esep-step-1">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  02
                </span>
                Approval Hierarchy
              </h5>
            </div>
            <div className="card-body p-3 p-md-4">
              <div
                className="alert alert-info border-0 d-flex align-items-center gap-2 mb-3 py-2 px-3 rounded-3"
                style={{ background: "#e8f4fd", color: "#084298" }}
              >
                <i className="ti ti-info-circle fs-5 flex-shrink-0" />
                <span className="small fw-semibold">
                  These details are auto-filled from your reporting hierarchy and cannot be edited.
                </span>
              </div>

              {/* Compact 2-column Grid */}
              <div className="row g-3">
                {/* Level 1: Controlling Officer */}
                <div className="col-lg-6 col-12">
                  <div className="p-3 bg-light rounded-3 border h-100">
                    <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                      <span
                        className="badge bg-primary text-white rounded-pill shadow-sm"
                        style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px" }}
                      >
                        Level 1
                      </span>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#212529" }}>
                        Controlling Officer (CO)
                      </span>
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                      <span
                        style={{
                          fontSize: "13.5px",
                          fontWeight: 700,
                          color: "#1a1f36",
                          lineHeight: 1.3,
                        }}
                      >
                        {prefill?.co_name || (prefill as any)?.co_emp_name || "—"}
                      </span>
                      <span
                        className="badge font-monospace shadow-sm"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          padding: "2px 6px",
                          color: "#0d6efd",
                          backgroundColor: "#f0f7ff",
                          border: "1px solid #b6d4fe",
                          borderRadius: "4px",
                        }}
                      >
                        Emp No: {prefill?.co_emp_no || (prefill as any)?.co_emp_id || (prefill as any)?.co_empno || "—"}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#6c757d",
                      }}
                    >
                      {prefill?.co_designation || "—"} &bull; {prefill?.co_department || "—"}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#6c757d",
                      }}
                      className="mt-1"
                    >
                      Mobile: {(prefill as any)?.co_mobile || "—"}
                    </div>
                  </div>
                </div>

                {/* Level 2: Dy. HOD */}
                <div className="col-lg-6 col-12">
                  <div className="p-3 bg-light rounded-3 border h-100">
                    <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                      <span
                        className="badge bg-primary text-white rounded-pill shadow-sm"
                        style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px" }}
                      >
                        Level 2
                      </span>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#212529" }}>
                        Dy. HOD
                      </span>
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                      <span
                        style={{
                          fontSize: "13.5px",
                          fontWeight: 700,
                          color: "#1a1f36",
                          lineHeight: 1.3,
                        }}
                      >
                        {prefill?.dy_hod_name || (prefill as any)?.dy_hod_emp_name || "—"}
                      </span>
                      <span
                        className="badge font-monospace shadow-sm"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          padding: "2px 6px",
                          color: "#0d6efd",
                          backgroundColor: "#f0f7ff",
                          border: "1px solid #b6d4fe",
                          borderRadius: "4px",
                        }}
                      >
                        Emp No: {prefill?.dy_hod_emp_no || (prefill as any)?.dy_hod_emp_id || (prefill as any)?.dy_hod_empno || "—"}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#6c757d",
                      }}
                    >
                      {prefill?.dy_hod_designation || "—"} &bull; {prefill?.dy_hod_department || "—"}
                    </div>
                  </div>
                </div>

                {/* Level 3: Head of Department (HOD) */}
                <div className="col-lg-6 col-12">
                  <div className="p-3 bg-light rounded-3 border h-100">
                    <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                      <span
                        className="badge bg-primary text-white rounded-pill shadow-sm"
                        style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px" }}
                      >
                        Level 3
                      </span>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#212529" }}>
                        Head of Department (HOD)
                      </span>
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                      <span
                        style={{
                          fontSize: "13.5px",
                          fontWeight: 700,
                          color: "#1a1f36",
                          lineHeight: 1.3,
                        }}
                      >
                        {prefill?.hod_name || (prefill as any)?.hod_emp_name || "—"}
                      </span>
                      <span
                        className="badge font-monospace shadow-sm"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          padding: "2px 6px",
                          color: "#0d6efd",
                          backgroundColor: "#f0f7ff",
                          border: "1px solid #b6d4fe",
                          borderRadius: "4px",
                        }}
                      >
                        Emp No: {prefill?.hod_emp_no || (prefill as any)?.hod_emp_id || (prefill as any)?.hod_empno || "—"}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#6c757d",
                      }}
                    >
                      {prefill?.hod_designation || "—"} &bull; {prefill?.hod_department || "—"}
                    </div>
                  </div>
                </div>

                {/* Level 4: Concerned Director (if applicable) */}
                {(earlyRelieving === "yes" || prefill?.director_name) && (
                  <div className="col-lg-6 col-12">
                    <div className="p-3 bg-light rounded-3 border h-100">
                      <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                        <span
                          className="badge bg-primary text-white rounded-pill shadow-sm"
                          style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px" }}
                        >
                          Level 4
                        </span>
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#212529" }}>
                          Concerned Director
                        </span>
                      </div>
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                        <span
                          style={{
                            fontSize: "13.5px",
                            fontWeight: 700,
                            color: "#1a1f36",
                            lineHeight: 1.3,
                          }}
                        >
                          {prefill?.director_name || (prefill as any)?.director_emp_name || "—"}
                        </span>
                        <span
                          className="badge font-monospace shadow-sm"
                          style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            padding: "2px 6px",
                            color: "#0d6efd",
                            backgroundColor: "#f0f7ff",
                            border: "1px solid #b6d4fe",
                            borderRadius: "4px",
                          }}
                        >
                          Emp No: {prefill?.director_emp_no || (prefill as any)?.director_emp_id || (prefill as any)?.director_empno || "—"}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#6c757d",
                        }}
                      >
                        {prefill?.director_designation || "—"} &bull; {prefill?.director_department || "—"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 03: NOTICE PERIOD (STEP 3) */}
        <div id="esep-step-2">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  03
                </span>
                Notice Period
              </h5>
            </div>
            <div className="card-body p-4">
              <div
                className="alert alert-info border-0 d-flex align-items-center gap-2 mb-4 py-2 px-3 rounded-3"
                style={{ background: "#e8f4fd", color: "#0c5460" }}
              >
                <i className="ti ti-clock fs-5 flex-shrink-0" />
                <span className="small fw-medium">
                  Notice period is 89 days as per DMRC policy.
                </span>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Date of Intimation
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={formatDate(intimationDate)}
                    readOnly
                  />
                  <small className="text-secondary fw-medium d-block mt-1">Auto-set to today's date (non-editable)</small>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Notice End Date
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={formatDate(displayedNoticeEndDate)}
                    readOnly
                  />
                  <small className="text-secondary fw-medium d-block mt-1">
                    {earlyRelieving === "yes" && desiredDate
                      ? "Updated to match your Desired Relieving Date (non-editable)."
                      : "Intimation date + 89 days (non-editable)."}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 04: EARLY RELIEVING REQUEST (STEP 4) */}
        <div id="esep-step-3">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  04
                </span>
                Early Relieving Request
              </h5>
            </div>
            <div className="card-body p-4">
              <label className="form-label fw-semibold text-secondary small mb-2">
                Do you want early relieving?
              </label>
              <div className="mb-3">
                <RadioGroup value={earlyRelieving} onChange={setEarlyRelieving} name="earlyRelieving" />
              </div>

              {earlyRelieving === "yes" && (
                <div className="p-3 bg-light rounded-3 border">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Desired Relieving Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        min={intimationDate}
                        max={noticeEndIso}
                        value={desiredDate}
                        onChange={(e) => setDesiredDate(e.target.value)}
                      />
                      <small className="text-secondary fw-medium d-block mt-1">
                        Must be before notice end date ({formatDate(noticeEndIso)})
                      </small>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Shortfall Days
                      </label>
                      <input
                        type="text"
                        className="form-control bg-white"
                        value={desiredDate ? `${shortfallDays} days` : "0 days"}
                        readOnly
                      />
                      <small className="text-secondary fw-medium d-block mt-1">
                        Days before notice completion
                      </small>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Estimated Short Notice Amount
                      </label>
                      <input
                        type="text"
                        className="form-control bg-white fw-bold text-danger"
                        value={`₹ ${shortNoticeAmountPreview.toFixed(2)}`}
                        readOnly
                      />
                      <small className="text-secondary fw-medium d-block mt-1">
                        Based on basic pay &amp; shortfall days
                      </small>
                    </div>
                  </div>
                  <div className="alert alert-warning border-0 d-flex align-items-center gap-2 mt-3 mb-0 py-2 px-3 rounded-3">
                    <i className="ti ti-alert-triangle fs-5 flex-shrink-0 text-warning" />
                    <span className="small text-dark fw-medium">
                      Short notice amount will be calculated and deducted from your final settlement as per DMRC service terms.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 05: BOND & CONTACT DETAILS (STEP 5) */}
        <div id="esep-step-4">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  05
                </span>
                Bond &amp; Contact Details
              </h5>
            </div>
            <div className="card-body p-4">
              {/* Service Bond Subsection */}
              {/* Dynamic Bond Types Subsection */}
              <div className="mb-4 pb-3 border-bottom">
                {bondTypes.length === 0 ? (
                  <small className="text-secondary fw-medium d-block mt-1">
                    No active bond types configured in master.
                  </small>
                ) : (
                  bondTypes.map((bt) => {
                    const sel = bondSelections[bt.id] || { active: false, values: {} };
                    return (
                      <div key={bt.id} className="mb-4">
                        <label className="form-label fw-semibold text-secondary small mb-2">
                          Are you under {bt.name} period? <span className="text-danger">*</span>
                        </label>
                        <RadioGroup
                          name={`bond-${bt.id}`}
                          value={sel.active ? "yes" : "no"}
                          onChange={(v) => {
                            setBondSelections({
                              ...bondSelections,
                              [bt.id]: { ...sel, active: v === "yes" }
                            });
                          }}
                        />
                        {sel.active && (
                          <div className="p-3 bg-light rounded-3 border mt-3">
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label className="form-label fw-semibold text-secondary small mb-1">
                                  Training Cost (₹) <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  className="form-control"
                                  placeholder="e.g. 50000"
                                  value={sel.values["training_cost"] || ""}
                                  onChange={(e) => {
                                    setBondSelections({
                                      ...bondSelections,
                                      [bt.id]: { ...sel, values: { ...sel.values, training_cost: e.target.value } }
                                    });
                                  }}
                                />
                              </div>
                              {bt.fields && bt.fields.length > 0 && bt.fields.map(f => (
                                <div key={f.field_key} className="col-md-6">
                                  <label className="form-label fw-semibold text-secondary small mb-1">
                                    {f.field_label} {f.is_required && <span className="text-danger">*</span>}
                                  </label>
                                  <input
                                    type={f.field_type === "NUMBER" ? "number" : "text"}
                                    className="form-control"
                                    value={sel.values[f.field_key] || ""}
                                    onChange={(e) => {
                                      setBondSelections({
                                        ...bondSelections,
                                        [bt.id]: { ...sel, values: { ...sel.values, [f.field_key]: e.target.value } }
                                      });
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Foreign Training */}
              <div className="row g-3 mb-4 pb-3 border-bottom">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small mb-2">
                    Have you undergone any Foreign Training and signed the bond?
                  </label>
                  <RadioGroup
                    value={foreignTrainingBond}
                    onChange={setForeignTrainingBond}
                    name="foreignTrainingBond"
                  />
                </div>
                {foreignTrainingBond === "yes" && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        (i) Period of Bond Executed <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        placeholder="e.g. 2 years"
                        value={foreignTrainingPeriod}
                        onChange={(e) => setForeignTrainingPeriod(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Amount of Bond Executed <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="₹"
                        value={foreignTrainingAmount}
                        onChange={(e) => setForeignTrainingAmount(e.target.value)}
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        (ii) Details of Training <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        placeholder="e.g. Course name, institute, country, dates"
                        value={foreignTrainingDetails}
                        onChange={(e) => setForeignTrainingDetails(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Training and Other Bond Subsection */}
              <div className="mb-4 pb-3 border-bottom">
                <div className="row g-4">
                  <div className="col-12">
                    <label className="form-label fw-semibold text-secondary small mb-2">
                      Training provided by DMRC (Induction/other course, India/Abroad) <span className="text-danger">*</span>
                    </label>
                    <RadioGroup
                      name="training-provided"
                      value={trainingProvided}
                      onChange={(v) => setTrainingProvided(v as string)}
                    />
                    {trainingProvided === "yes" && (
                      <textarea
                        className="form-control mt-2"
                        rows={2}
                        placeholder="Training details"
                        value={trainingDetails}
                        onChange={(e) => setTrainingDetails(e.target.value)}
                      />
                    )}
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold text-secondary small mb-2">
                      Any other Bond submitted to DMRC (other than Joining Bond) <span className="text-danger">*</span>
                    </label>
                    <RadioGroup
                      name="other-bond"
                      value={otherBond}
                      onChange={(v) => setOtherBond(v as string)}
                    />
                    {otherBond === "yes" && (
                      <textarea
                        className="form-control mt-2"
                        rows={2}
                        placeholder="Other bond details"
                        value={otherBondDetails}
                        onChange={(e) => setOtherBondDetails(e.target.value)}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Information Subsection */}
              <div>
                <h6 className="fw-bold text-dark mb-3">Contact Information</h6>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Correspondence / Present Address
                    </label>
                    <input
                      className="form-control"
                      placeholder="Enter present address"
                      value={tempAddress}
                      onChange={(e) => setTempAddress(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 06: REASON & ATTACHMENTS (STEP 6) */}
        <div id="esep-step-5">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  06
                </span>
                Reason for Separation &amp; Supporting Documents
              </h5>
            </div>
            <div className="card-body p-4">
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label fw-semibold text-secondary small mb-0">
                    Reason for Resignation <span className="text-danger">*</span>
                  </label>
                  <span className="small text-muted">{reason.length}/500</span>
                </div>
                <textarea
                  className="form-control"
                  rows={4}
                  maxLength={500}
                  placeholder="Please provide detailed reason for resignation / separation..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, 500))}
                />
              </div>

              <div className="row g-3 mb-4 pb-3 border-bottom">
                <div className="col-12">
                  <label className="form-label fw-semibold text-secondary small mb-2">
                    Whether the NOC for Out Side Job is obtained, If yes, mention Post, Advt.no. and date, attach copy of NOC also
                  </label>
                  <RadioGroup value={noc} onChange={setNoc} name="noc" />
                </div>
                {noc === "yes" && (
                  <div className="row g-3 mt-1">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Post Applied For
                      </label>
                      <input
                        className="form-control"
                        placeholder="Post / Position"
                        value={nocPost}
                        onChange={(e) => setNocPost(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Advertisement No.
                      </label>
                      <input
                        className="form-control"
                        placeholder="Advt. No."
                        value={nocAdvtNo}
                        onChange={(e) => setNocAdvtNo(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={nocDate}
                        onChange={(e) => setNocDate(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 pb-3 border-bottom">
                <label className="form-label fw-semibold text-secondary small mb-2">
                  Upload Certificates / Supporting Documents {noc === "yes" && <span className="text-danger">*</span>}
                </label>
                <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
                  <input
                    type="file"
                    id="supportingDocsInput"
                    className="d-none"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
                    onChange={(e) => handleUpload(e.target.files)}
                    disabled={uploading}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center gap-2"
                    onClick={() => document.getElementById("supportingDocsInput")?.click()}
                    disabled={uploading}
                  >
                    <i className="ti ti-upload"></i> {uploading ? "Uploading..." : "Upload Documents"}
                  </button>
                  <span className="text-secondary small">PDF, JPG, PNG — max 5 MB each</span>
                </div>
                {uploadedDocs.length > 0 && (
                  <div className="mt-3">
                    <label className="form-label fw-semibold text-secondary small mb-1">Uploaded Files:</label>
                    <div className="d-flex flex-column gap-2">
                      {uploadedDocs.map((d, i) => (
                        <div
                          key={d.s3_key + i}
                          className="bg-light border border-success-subtle rounded px-3 py-2 d-flex align-items-center justify-content-between shadow-sm"
                          style={{ maxWidth: "500px" }}
                        >
                          <div
                            className="d-flex align-items-center gap-2 text-dark overflow-hidden me-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => viewDoc(d)}
                            title="Click to view file"
                          >
                            <i className="ti ti-file-check text-success fs-5"></i>
                            <span className="fw-medium small text-primary text-decoration-underline text-truncate" title={d.file_name}>
                              {d.file_name}
                            </span>
                          </div>
                          <div className="d-flex gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary p-1 d-flex align-items-center justify-content-center"
                              onClick={() => viewDoc(d)}
                              title="View Document"
                              style={{ width: "26px", height: "26px" }}
                            >
                              <i className="ti ti-eye" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger p-1 d-flex align-items-center justify-content-center"
                              onClick={() => removeDoc(i)}
                              title="Remove Document"
                              style={{ width: "26px", height: "26px" }}
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 pb-3 border-bottom">
                <label className="form-label fw-semibold text-secondary small mb-2">
                  Educational Qualification in service record (In case higher education obtained after joining, attach NOC/addition order)
                </label>
                {eduRows.map((row) => (
                  <div className="row g-3 mb-2" key={row.key}>
                    <div className="col-md-6">
                      <input
                        className="form-control"
                        placeholder="e.g. B.Tech, MBA, M.Sc."
                        value={row.name}
                        onChange={(e) => patchEdu(row.key, { name: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 d-flex align-items-center gap-3 flex-wrap">
                      <input
                        type="file"
                        id={`eduQualDocInput-${row.key}`}
                        className="d-none"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          handleEduQualUpload(row.key, e.target.files);
                          e.target.value = "";
                        }}
                        disabled={row.uploading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                        onClick={() => document.getElementById(`eduQualDocInput-${row.key}`)?.click()}
                        disabled={row.uploading}
                      >
                        <i className="ti ti-upload"></i> {row.uploading ? "Uploading..." : "Upload Document"}
                      </button>
                      {row.doc && !row.uploading && (
                        <div className="d-flex align-items-center gap-2 bg-light border rounded px-2 py-1 shadow-sm">
                          <div
                            className="d-flex align-items-center gap-2 overflow-hidden"
                            style={{ cursor: "pointer", maxWidth: "200px" }}
                            onClick={() => row.doc && viewDoc(row.doc)}
                            title="Click to view file"
                          >
                            <i className="ti ti-file-check text-success"></i>
                            <span className="fw-medium small text-primary text-decoration-underline text-truncate" title={row.doc.file_name}>
                              {row.doc.file_name}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-icon text-danger m-0 p-0"
                            onClick={() => patchEdu(row.key, { doc: null })}
                            title="Remove"
                          >
                            <i className="ti ti-trash"></i>
                          </button>
                        </div>
                      )}
                      {eduRows.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 ms-auto"
                          onClick={() => removeEdu(row.key)}
                          title="Remove this qualification"
                        >
                          <i className="ti ti-x"></i>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 mt-1"
                  onClick={addEdu}
                >
                  <i className="ti ti-plus"></i> Add Qualification
                </button>
              </div>

              {/* Outstanding Loans Subsection */}
              <div className="mb-4 pb-3 border-bottom">
                <h6 className="fw-bold text-dark mb-2">Outstanding Loans</h6>
                <OutstandingLoansSection
                  loans={outstandingLoans}
                  loading={prefillLoading}
                  overrides={loanOverrides}
                  onOverrideChange={(ref, value) =>
                    setLoanOverrides((prev) => ({ ...prev, [ref]: value }))
                  }
                />
              </div>

              {/* Indicate Loan or Advances (field 25) */}
              <div className="mb-4 pb-3 border-bottom">
                <h6 className="fw-bold text-dark mb-3">Indicate Loan or Advances</h6>
                <div className="row g-3">
                  {DECLARED_LOAN_TYPES.map((lt) => (
                    <div key={lt.code} className="col-md-4 col-sm-6">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        {lt.label}
                      </label>
                      <input
                        type="number"
                        min={0}
                        className="form-control"
                        placeholder="Rs."
                        value={declaredLoans[lt.code] ?? ""}
                        onChange={(e) =>
                          setDeclaredLoans((prev) => ({ ...prev, [lt.code]: e.target.value }))
                        }
                      />
                    </div>
                  ))}
                  {Number(declaredLoans["OTHERS"]) > 0 && (
                    <div className="col-md-8">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Please specify "Others"
                      </label>
                      <input
                        className="form-control"
                        placeholder="Describe the loan/advance"
                        value={otherLoanDescription}
                        onChange={(e) => setOtherLoanDescription(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Your Sureties for Loans (field 26) */}
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold text-dark mb-0">Your Sureties for Loans</h6>
                </div>
                <small className="text-secondary fw-medium d-block mb-2">
                  Auto-fetched from your loan records where available — you may correct the
                  employee ID or add additional sureties.
                </small>
                {prefillLoading ? (
                  <p className="text-muted fst-italic mb-0">Loading…</p>
                ) : suretyRows.length === 0 ? (
                  <p className="text-muted fst-italic mb-2">No sureties on record.</p>
                ) : null}
                <div className="d-flex flex-column gap-2 mb-2">
                  {suretyRows.map((row, i) => (
                    <div key={i} className="row g-2 align-items-start">
                      <div className="col-md-4">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Surety for Loan / Advance Name
                        </label>
                        <input
                          className="form-control"
                          placeholder="e.g. Multi Purpose Advance"
                          value={row.loan_type}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSuretyRows((prev) =>
                              prev.map((r, idx) => (idx === i ? { ...r, loan_type: v } : r))
                            );
                          }}
                        />
                      </div>
                      <div className="col-md-7">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Surety Employee
                        </label>
                        <EmployeeSearchInput
                          value={row.surety_emp_id}
                          placeholder="Type employee number or name…"
                          onChange={(v) =>
                            setSuretyRows((prev) =>
                              prev.map((r, idx) =>
                                idx === i ? { ...r, surety_emp_id: v, surety_emp_name: undefined } : r
                              )
                            )
                          }
                          onSelect={(emp: EmployeeSearchResult) =>
                            setSuretyRows((prev) =>
                              prev.map((r, idx) =>
                                idx === i
                                  ? { ...r, surety_emp_id: emp.emp_no, surety_emp_name: emp.emp_name }
                                  : r
                              )
                            )
                          }
                        />
                        {row.surety_emp_name && (
                          <small className="text-muted d-block mt-1">{row.surety_emp_name}</small>
                        )}
                      </div>
                      <div className="col-md-1 d-flex flex-column">
                        <label className="form-label small mb-1 invisible">Remove</label>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setSuretyRows((prev) => prev.filter((_, idx) => idx !== i))}
                          title="Remove"
                        >
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() =>
                    setSuretyRows((prev) => [
                      ...prev,
                      { loan_type: "", surety_emp_id: "" },
                    ])
                  }
                >
                  <i className="ti ti-plus me-1" />
                  Add Surety
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 07: DECLARATIONS & HANDOVER DETAILS (STEP 7) */}
        <div id="esep-step-6">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  07
                </span>
                Declarations &amp; Handover Details
              </h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-3 mb-4 pb-3 border-bottom">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small mb-2">
                    Any item like Tools / Computer / Laptop / Library books in your name?
                  </label>
                  <RadioGroup value={itemsInCustody} onChange={setItemsInCustody} name="itemsInCustody" />
                </div>
                {itemsInCustody === "yes" && (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Item Name(s) <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      placeholder="e.g. Laptop, ID Card Reader, Books"
                      value={itemsInCustodyText}
                      onChange={(e) => setItemsInCustodyText(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="row g-3 mb-4 pb-3 border-bottom">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Nominated officer/employee to take temporary charge from you
                  </label>
                  <input
                    className="form-control"
                    placeholder="Name / Employee No."
                    value={tempChargeOfficer}
                    onChange={(e) => setTempChargeOfficer(e.target.value)}
                  />
                </div>
              </div>



              {isFemaleEmployee && (
                <div className="row g-3 mb-4 pb-3 border-bottom">
                  <div className="col-12">
                    <label className="form-label fw-semibold text-secondary small mb-2 d-block">
                      Child Care Leave (CCL) taken in last 2 years?
                    </label>
                    <LeaveRecordsDisplay records={cclRecords} loading={prefillLoading} />
                  </div>
                </div>
              )}

              <div className="row g-3 mb-4 pb-3 border-bottom">
                <div className="col-12">
                  <label className="form-label fw-semibold text-secondary small mb-2 d-block">
                    Leave Without Pay (LWP) taken in last 2 years?
                  </label>
                  <LeaveRecordsDisplay records={lwpRecords} loading={prefillLoading} />
                </div>
              </div>

              <div className="row g-3 mb-4 pb-3 border-bottom">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small mb-2">
                    Extraordinary Leave (EOL) availed?
                  </label>
                  <RadioGroup value={eolAvailed} onChange={setEolAvailed} name="eolAvailed" />
                </div>
                {eolAvailed === "yes" && (
                  <div className="col-12 mt-3">
                    <label className="form-label fw-semibold text-secondary small mb-2">
                      Duration &amp; Exact Dates <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex flex-column gap-2">
                      {eolPeriods.map((period, index) => (
                        <div key={index} className="d-flex align-items-center gap-2">
                          <input
                            type="date"
                            className="form-control"
                            value={period.start}
                            onChange={(e) => {
                              const newP = [...eolPeriods];
                              newP[index].start = e.target.value;
                              setEolPeriods(newP);
                            }}
                          />
                          <span className="text-muted fw-medium small">to</span>
                          <input
                            type="date"
                            className="form-control"
                            value={period.end}
                            onChange={(e) => {
                              const newP = [...eolPeriods];
                              newP[index].end = e.target.value;
                              setEolPeriods(newP);
                            }}
                          />
                          {index === eolPeriods.length - 1 ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                              onClick={() => setEolPeriods([...eolPeriods, { start: "", end: "" }])}
                              style={{ width: "38px", height: "38px" }}
                              title="Add another period"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                              onClick={() => setEolPeriods(eolPeriods.filter((_, i) => i !== index))}
                              style={{ width: "38px", height: "38px" }}
                              title="Remove period"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="row g-3 mb-4 pb-3 border-bottom">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small mb-2">
                    Have you sought and withdrawn your resignation in last 12 months?
                  </label>
                  <RadioGroup value={withdrawLastYr} onChange={setWithdrawLastYr} name="withdrawLastYr" />
                </div>
                {withdrawLastYr === "yes" && (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Details <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      placeholder="Provide details of the withdrawn resignation"
                      value={withdrawLastYrDetails}
                      onChange={(e) => setWithdrawLastYrDetails(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="row g-3 mb-4 pb-3 border-bottom">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small mb-2">
                    Have you ever been on deputation?
                  </label>
                  <RadioGroup
                    value={deputationAvailed}
                    onChange={setDeputationAvailed}
                    name="deputationAvailed"
                  />
                </div>
                {deputationAvailed === "yes" && (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-secondary small mb-2">
                      Have all remittances from deputation service been received in DMRC?
                    </label>
                    <RadioGroup
                      value={deputationRemittanceReceived}
                      onChange={setDeputationRemittanceReceived}
                      name="deputationRemittanceReceived"
                    />
                  </div>
                )}

                {deputationAvailed === "yes" && (
                  <div className="col-12">
                    <label className="form-label fw-semibold text-secondary small mb-2">
                      Deputation Period(s), Place(s) &amp; Organization
                    </label>
                    {deputationHistory.map((row, i) => (
                      <div className="row g-2 mb-2" key={i}>
                        <div className="col-md-6">
                          <input
                            className="form-control"
                            placeholder="Organization name"
                            value={row.organization_name}
                            onChange={(e) =>
                              updateDeputationRow(i, "organization_name", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-md-5">
                          <input
                            className="form-control"
                            placeholder="Duration (e.g. Jan 2020 – Dec 2022)"
                            value={row.duration}
                            onChange={(e) => updateDeputationRow(i, "duration", e.target.value)}
                          />
                        </div>
                        <div className="col-md-1 d-flex align-items-center">
                          {deputationHistory.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => removeDeputationRow(i)}
                            >
                              <i className="ti ti-trash" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm mt-1"
                      onClick={addDeputationRow}
                    >
                      <i className="ti ti-plus me-1" />
                      Add Another Deputation
                    </button>
                  </div>
                )}
              </div>

              <div className="mb-4 pb-3 border-bottom">
                <label className="form-label fw-semibold text-secondary small mb-1">
                  Furnish details of any Court Case, Vigilance case and D&amp;AR proceedings
                  pending against you
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Write NIL if none pending"
                  value={pendingCasesDetails}
                  onChange={(e) => setPendingCasesDetails(e.target.value)}
                />
              </div>


              <div className="row g-3 mb-4 pb-3 border-bottom">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Release/Transfer of Gratuity and Leave Encashment <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={gratuityOption}
                    onChange={(e) => setGratuityOption(e.target.value as "release" | "transfer")}
                  >
                    <option value="release">Release</option>
                    <option value="transfer">Transfer</option>
                  </select>
                  <small className="text-secondary fw-medium d-block mt-1">
                    Applicable only for selection through proper channel.
                  </small>
                </div>
              </div>

              {/* Declaration */}
              <div className="p-3 bg-light rounded-3 border">
                <div className="fw-bold text-dark mb-3">Declaration:</div>
                <ol className="ps-3 mb-0" style={{ listStyleType: "upper-roman" }}>
                  <li className="mb-3 small text-dark">
                    I hereby submit that the details provided above are correct, and I request
                    that my resignation from the services of DMRC be accepted. I may kindly be
                    relieved from the services (last working day in DMRC) with effect from{" "}
                    <strong>{formatDate(displayedNoticeEndDate)}</strong>.
                  </li>
                  <li className="mb-3 small text-dark">
                    I further undertake that I shall not leave my duties in the organization
                    before the acceptance of my resignation by the Competent Authority. In case
                    of any violation, I understand that I will be liable for disciplinary action
                    under 15(j) of General Conditions of Service Rules of DMRC.
                  </li>
                  <li className="mb-3 small text-dark">
                    Further, I hereby declare that after acceptance of my resignation and
                    relieving from DMRC, I shall not join or take up any assignment with any
                    contractor, firm, company having direct or indirect working relationship
                    with DMRC within 2 years of acceptance of my resignation (i.e. last working
                    day in DMRC).
                  </li>
                  <li className="mb-0 small text-dark">
                    In case of short notice period, I will pay the amount toward unserved notice
                    period (no. of days) or indicated in point 4.
                  </li>
                </ol>
                <div className="form-check mt-3 pt-3 border-top">
                  <input
                    type="checkbox"
                    className="form-check-input mt-1"
                    id="declaration"
                    checked={declared}
                    onChange={(e) => setDeclared(e.target.checked)}
                  />
                  <label className="form-check-label small fw-semibold text-dark ms-1" htmlFor="declaration">
                    I have read and agree to the above declaration.
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ACTION BUTTONS BAR */}
        <div
          className="card shadow-sm border-0 sticky-bottom py-3 px-4"
          style={{ borderRadius: 12, bottom: 20, zIndex: 10, background: "#fff" }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="small text-dark fw-medium d-flex align-items-center gap-2">
              <i className="ti ti-info-circle fs-5 text-primary" />
              <span>Ensure all details and declarations are reviewed before submission.</span>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary px-4 py-2"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary px-4 py-2 d-flex align-items-center gap-2"
                onClick={handleSubmit}
                disabled={!declared || submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ti ti-send" /> Submit Intimation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntimationResignation;
